import { useState } from 'react'
import { Alert, Button, Form, Input, Modal, Space } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { RowActions } from '@/shared/ui/table/RowActions'
import { setVenueState, type Venue, type VenueState } from './venueApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface VenueStateActionProps {
  venue: Venue
  onChanged?: (venue: Venue) => void
  size?: 'small' | 'middle'
  /**
   * JADVAL QATORIDA `menu` — uchta tugma bitta "uch nuqta" menyusiga
   * yig'iladi va ustun 56px ga qisqaradi.
   *
   * ⚠️ Sukut `buttons` — kartochka sarlavhasidagi ko'rinish (`VenueDetailPage`)
   * O'ZGARMAYDI. Huquq (`venue.update`) va holat shartlari ikkala rejimda
   * ham AYNAN bir xil, faqat chizilishi boshqacha.
   */
  variant?: 'buttons' | 'menu'
}

/**
 * Ob'ekt holatini o'zgartirish tugmalari.
 *
 * ⚠️ `SUSPENDED`da mavjud grantlar BEKOR QILINMAYDI (`VenueState.java`
 * javadoc) — faqat yangi mehmon qabul qilinmaydi. Buni tugma matnida
 * aytamiz, aks holda operator "to'xtatdim" tugmasini bosib, hozir ulangan
 * mehmonlarning uzilib qolishini kutishi mumkin.
 */
export function VenueStateAction({
  venue,
  onChanged,
  size = 'middle',
  variant = 'buttons',
}: VenueStateActionProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [pendingState, setPendingState] = useState<VenueState | null>(null)
  const [form] = Form.useForm<{ reason?: string }>()

  const change = useMutation({
    mutationFn: (values: { state: VenueState; reason?: string }) =>
      setVenueState(venue.id, values.state, values.reason),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.venues.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.venues.detail(venue.id) })
      setPendingState(null)
      form.resetFields()
      onChanged?.(saved)
    },
  })

  /**
   * ⚠️ Xato mutatsiyaning O'ZIDA turadi (`VoucherVoidModal` naqshi), alohida
   * `useState` da emas: ikkinchi nusxa har ochish-yopishda qo'lda tozalanishi
   * kerak edi va bir joyda unutilsa ekranda begona xabar qolardi.
   */
  const openReasonModal = (state: VenueState) => {
    change.reset()
    setPendingState(state)
  }

  const close = () => {
    change.reset()
    setPendingState(null)
  }

  const submit = () => {
    submitOnValid(form, (values) => {
      if (!pendingState) return
      change.mutate({ state: pendingState, reason: values.reason?.trim() || undefined })
    })
  }

  return (
    <>
      {variant === 'menu' ? (
        <RowActions
          items={[
            {
              key: 'activate',
              label: t('venue.activate'),
              onClick: () => openReasonModal('ACTIVE'),
              permission: 'venue.update',
              hidden: venue.state !== 'SUSPENDED',
            },
            {
              key: 'suspend',
              label: t('venue.suspend'),
              onClick: () => openReasonModal('SUSPENDED'),
              permission: 'venue.update',
              hidden: venue.state !== 'ACTIVE',
            },
            {
              key: 'close',
              label: t('venue.close'),
              onClick: () => openReasonModal('CLOSED'),
              permission: 'venue.update',
              danger: true,
              divider: true,
              hidden: venue.state === 'CLOSED',
            },
          ]}
        />
      ) : (
      <Space wrap size="small">
        {venue.state === 'SUSPENDED' && (
          <Can permission="venue.update">
            <Button size={size} type="primary" onClick={() => openReasonModal('ACTIVE')}>
              {t('venue.activate')}
            </Button>
          </Can>
        )}
        {venue.state === 'ACTIVE' && (
          <Can permission="venue.update">
            <Button size={size} onClick={() => openReasonModal('SUSPENDED')}>
              {t('venue.suspend')}
            </Button>
          </Can>
        )}
        {venue.state !== 'CLOSED' && (
          <Can permission="venue.update">
            <Button size={size} danger onClick={() => openReasonModal('CLOSED')}>
              {t('venue.close')}
            </Button>
          </Can>
        )}
      </Space>
      )}

      <Modal
        open={pendingState !== null}
        title={pendingState ? t(`venue.stateTitle.${pendingState}`) : ''}
        onCancel={close}
        onOk={submit}
        confirmLoading={change.isPending}
        okText={t('common.done')}
        cancelText={t('common.cancel')}
        okButtonProps={{ danger: pendingState === 'CLOSED' }}
        destroyOnHidden
      >
        {pendingState === 'SUSPENDED' ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('venue.suspendKeepsGrantsTitle')}
            description={t('venue.suspendKeepsGrantsHint')}
          />
        ) : null}
        {pendingState === 'CLOSED' ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('venue.closeIrreversibleTitle')}
          />
        ) : null}

        {change.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(change.error, t)}
          />
        ) : null}

        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="reason" label={t('actions.reason')} extra={t('actions.reasonHint')}>
            <Input.TextArea rows={3} maxLength={200} autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
